import { normalizePlayerYaw, clampPlayerPitch, PlayerGait, PlayerStance, type ClientInput, type PlayerControlIntent } from "@endclock/shared";
import {
  DEFAULT_APPEARANCE,
  DEFAULT_DEVELOPER_STATE,
  ITEM_CATALOG_HASH,
  ITEM_CATALOG_VERSION,
  PROTOCOL_VERSION,
  isServerEnvironment, isServerLightning,
  type ServerEnvironment, type LightningEvent,
  WORLD_GENERATION_SEED,
  WORLD_GENERATION_VERSION,
  isDroneId,
  isPackedPlayer,
  classifyWaterLocomotion, sampleWorldWaterAt,
  isDroneShoutText,
  isDroneSnapshotList,
  isServerDroneTelemetry,
  isServerDroneShoutResult,
  type ClientActivity,
  type ServerGuestStatus,
  type ClientNpcCommand,
  type LiveNpcSnapshot,
  type ServerNpcMessage,
  type PackedDrone,
  type ServerDroneTelemetry,
  type ServerDroneShoutResult,
  type CharacterAppearance,
  type ClientMessage,
  type ContainerTransferDirection,
  type DeveloperSpeed,
  type DeveloperState,
  type DeveloperTeleportResult,
  type DeveloperTeleportTargetId,
  type InteractionActionId,
  type InteractionTargetRef,
  type PackedPlayer,
  type ServerInteractionCancelled,
  type ServerInteractionCompleted,
  type ServerInteractionRejected,
  type ServerInteractionStarted,
  type ServerMessage,
  type ServerWorldOverrideDelta,
  type ServerWorldOverrideSync,
  type ClientPhysicalAction,
  type PocketRef,
  type PackedEquipment,
} from "@endclock/shared";
import type { InventoryReplicationMessage } from "../items/inventory-store.js";
import { isServerCombatSnapshot, isServerCombatEvents, isServerPlayerCondition,
  type ServerCombatSnapshot, type ServerCombatEvents, type ServerPlayerCondition, type ClientDeveloperCommand } from "@endclock/shared";

export type ConnectionState = "offline" | "connecting" | "online" | "reconnecting";

export interface WebSocketPageOrigin {
  readonly protocol: string;
  readonly host: string;
}

export function resolvePublicWebSocketOverride(
  publicOverride: string | null,
  trustedFallback: string | undefined,
  pageOrigin: WebSocketPageOrigin,
): string | undefined {
  const fallback = trustedFallback || undefined;
  const candidate = publicOverride?.trim();
  if (!candidate) return fallback;

  const expectedProtocol = pageOrigin.protocol === "https:"
    ? "wss:"
    : pageOrigin.protocol === "http:"
      ? "ws:"
      : undefined;
  if (!expectedProtocol || !pageOrigin.host) return fallback;
  if (!candidate.startsWith("/") && !/^wss?:\/\//i.test(candidate)) return fallback;

  try {
    const parsed = new URL(candidate, `${expectedProtocol}//${pageOrigin.host}/`);
    if (
      parsed.protocol !== expectedProtocol
      || parsed.host !== pageOrigin.host
      || parsed.username
      || parsed.password
      || parsed.hash
    ) {
      return fallback;
    }
    return parsed.href;
  } catch {
    return fallback;
  }
}

export interface NetworkSnapshot {
  readonly self: HydratedPlayer | undefined;
  readonly others: readonly HydratedPlayer[];
  readonly generation: number;
  readonly tick: number;
  readonly serverAt: number;
  readonly receivedAt: number;
}

export type HydratedPlayer = readonly [...PackedPlayer, appearance: CharacterAppearance];

export class NetworkClient {
  state: ConnectionState = "offline";
  ping = 0;
  sessionId = "";
  latest: NetworkSnapshot = { self: undefined, others: [], generation: 0, tick: -1, serverAt: 0, receivedAt: 0 };
  bytesSent = 0;
  bytesReceived = 0;
  onStateChange: ((state: ConnectionState) => void) | undefined;
  onDeveloperState: ((state: DeveloperState, teleport?: DeveloperTeleportResult) => void) | undefined;
  onDeveloperError: ((code: string) => void) | undefined;
  onInteractionStarted: ((message: ServerInteractionStarted) => void) | undefined;
  onInteractionCompleted: ((message: ServerInteractionCompleted) => void) | undefined;
  onInteractionCancelled: ((message: ServerInteractionCancelled) => void) | undefined;
  onInteractionRejected: ((message: ServerInteractionRejected) => void) | undefined;
  onWorldOverrideSync: ((message: ServerWorldOverrideSync) => void) | undefined;
  onWorldOverrideDelta: ((message: ServerWorldOverrideDelta) => void) | undefined;
  onInventoryMessage: ((message: InventoryReplicationMessage) => void) | undefined;
  onUpdateRequired: (() => void) | undefined;
  onSessionReset: (() => void) | undefined;
  onGuestEntryRequired: (() => void) | undefined;
  onGuestStatus: ((message: ServerGuestStatus) => void) | undefined;
  onNpcSnapshot: ((npcs: readonly LiveNpcSnapshot[], at: number, tick: number) => void) | undefined;
  onNpcMessage: ((message: ServerNpcMessage) => void) | undefined;
  onNpcReset: (() => void) | undefined;
  onDroneSnapshot: ((drones: readonly PackedDrone[], at: number) => void) | undefined;
  onDroneTelemetry: ((message: ServerDroneTelemetry) => void) | undefined;
  onDroneShoutResult: ((message: ServerDroneShoutResult) => void) | undefined;
  onDroneReset: (() => void) | undefined;
  onEnvironment: ((message: ServerEnvironment) => void) | undefined;
  onEquipment: ((entries: readonly PackedEquipment[]) => void) | undefined;
  onLightning: ((event: LightningEvent) => void) | undefined;
  onCombat: ((message: ServerCombatSnapshot) => void) | undefined;
  onCombatEvents: ((message: ServerCombatEvents) => void) | undefined;
  onCondition: ((message: ServerPlayerCondition) => void) | undefined;
  combatBytesReceived = 0;
  conditionBytesReceived = 0;
  developer: DeveloperState = DEFAULT_DEVELOPER_STATE;
  reconnectCount = 0;
  private socket: WebSocket | undefined;
  private reconnectTimer: number | undefined;
  private pingTimer: number | undefined;
  private retry = 0;
  private handshakeFailures = 0;
  private sequence = 0;
  private droneRequestSequence = 0;
  private pendingDeveloperPassword: string | undefined;
  private connectionGeneration = 0;
  private serverTickRate = 20;
  private desired = false;
  private readonly appearances = new Map<string, CharacterAppearance>();

  constructor(private appearance: CharacterAppearance, private readonly explicitUrl?: string) {}

  connect(): void {
    this.desired = true;
    if (this.socket?.readyState === WebSocket.OPEN || this.socket?.readyState === WebSocket.CONNECTING) return;
    this.connectionGeneration += 1;
    this.resetSession();
    this.setState(this.retry > 0 ? "reconnecting" : "connecting");
    const url = this.explicitUrl ?? `${location.protocol === "https:" ? "wss" : "ws"}://${location.host}/ws`;
    const socket = new WebSocket(url);
    this.socket = socket;
    socket.addEventListener("open", () => {
      this.retry = 0;
      this.send({
        t: "hello",
        v: PROTOCOL_VERSION,
        itemCatalogVersion: ITEM_CATALOG_VERSION,
        itemCatalogHash: ITEM_CATALOG_HASH,
        appearance: this.appearance,
      });
      if (this.pendingDeveloperPassword) {
        this.send({ t: "dev_auth", password: this.pendingDeveloperPassword });
        this.pendingDeveloperPassword = undefined;
      }
    });
    socket.addEventListener("message", (event) => { if (socket === this.socket) this.receive(String(event.data)); });
    socket.addEventListener("close", (event) => {
      if (socket !== this.socket) return;
      if (event.reason === "SESSION_ENDED") {
        this.desired = false;
        this.onGuestEntryRequired?.();
      }
      if (event.reason === "IDLE_TIMEOUT") {
        this.desired = false;
        this.onGuestStatus?.({ t: "guest_status", idleRemainingMs: 0, reason: "IDLE_TIMEOUT" });
      }
      this.disconnected();
    });
    socket.addEventListener("error", () => socket.close());
  }

  disconnect(): void {
    this.desired = false;
    if (this.reconnectTimer !== undefined) window.clearTimeout(this.reconnectTimer);
    if (this.pingTimer !== undefined) window.clearInterval(this.pingTimer);
    this.socket?.close(1000, "Leaving world");
    this.socket = undefined;
    this.resetSession();
    this.setState("offline");
  }

  setGuestNickname(nickname: string): void {
    this.appearance = { ...this.appearance, name: nickname };
  }

  reportActivity(kind: ClientActivity["kind"]): void {
    if (this.state === "online") this.send({ t: "activity", kind });
  }

  sendNpcCommand(message: ClientNpcCommand): void {
    if (this.state === "online") this.send(message);
  }

  forceReconnect(): void {
    this.socket?.close(4001, "Connection restart test");
  }

  subscribeDrone(droneId: string | null): void {
    if (droneId !== null && !isDroneId(droneId)) return;
    this.send({ t: "drone_subscribe", droneId });
  }

  shoutAtDrone(droneId: string, text: string): string | null {
    if (!isDroneId(droneId) || !isDroneShoutText(text) || this.state !== "online") return null;
    const requestId = `drone_${this.connectionGeneration}_${++this.droneRequestSequence}`;
    this.send({ t: "drone_shout", droneId, requestId, text });
    return requestId;
  }

  estimatedServerNow(now = performance.now()): number {
    return this.latest.tick < 0 ? Date.now() : this.latest.serverAt + Math.max(0, now - this.latest.receivedAt);
  }

  authorizeDeveloper(password: string): void {
    const normalized = password.trim();
    if (!normalized) return;
    if (this.socket?.readyState === WebSocket.OPEN) this.send({ t: "dev_auth", password: normalized });
    else this.pendingDeveloperPassword = normalized;
  }

  setDeveloperNoclip(enabled: boolean): void {
    if (this.developer.authorized) this.send({ t: "dev_cmd", command: "noclip", enabled });
  }

  setDeveloperFreeFly(enabled: boolean): void {
    if (this.developer.authorized) this.send({ t: "dev_cmd", command: "fly", enabled });
  }

  setDeveloperSpeed(speed: DeveloperSpeed): void {
    if (this.developer.authorized) this.send({ t: "dev_cmd", command: "speed", speed });
  }

  teleportDeveloper(target: DeveloperTeleportTargetId): void {
    if (this.developer.authorized) this.send({ t: "dev_cmd", command: "teleport", target });
  }

  teleportDeveloperCoordinates(x: number, z: number, y?: number): void {
    if (!this.developer.authorized) return;
    this.send({ t: "dev_cmd", command: "teleport", x, z, ...(y === undefined ? {} : { y }) });
  }

  resetDeveloper(): void {
    if (this.developer.authorized) this.send({ t: "dev_cmd", command: "reset" });
  }

  setAppearance(appearance: CharacterAppearance): void {
    this.appearance = appearance;
    if (this.socket?.readyState === WebSocket.OPEN) this.send({
      t: "hello",
      v: PROTOCOL_VERSION,
      itemCatalogVersion: ITEM_CATALOG_VERSION,
      itemCatalogHash: ITEM_CATALOG_HASH,
      appearance,
    });
  }

  sendControl(control: PlayerControlIntent): ClientInput | undefined {
    if (this.state !== "online" || this.socket?.readyState !== WebSocket.OPEN) return undefined;
    this.sequence += 1;
    const magnitude = Math.max(1, Math.hypot(control.x, control.z));
    const command: ClientInput = { t: "i", s: this.sequence, e: this.latest.self?.[21] ?? 0,
      x: round(control.x / magnitude), z: round(control.z / magnitude), run: control.gait === PlayerGait.Walk ? 0 : 1,
      aimYaw: normalizePlayerYaw(Math.round(normalizePlayerYaw(control.aimYaw) * 100000) / 100000),
      aimPitch: clampPlayerPitch(Math.round(clampPlayerPitch(control.aimPitch) * 100000) / 100000),
      stance: control.stance, gait: control.gait, actions: control.actions & 15,
      ...(control.vertical ? { vertical: control.vertical } : {}) };
    this.send(command);
    return command;
  }

  sendInput(x: number, z: number, run: boolean, vertical: -1 | 0 | 1 = 0): number {
    return this.sendControl({ x, z, aimYaw: this.latest.self?.[11] ?? 0, aimPitch: this.latest.self?.[12] ?? 0,
      stance: PlayerStance.Standing, gait: run ? PlayerGait.Run : PlayerGait.Walk, actions: 0, vertical })?.s ?? this.sequence;
  }

  startInteraction(requestId: string, target: InteractionTargetRef, actionId: InteractionActionId): void {
    this.send({ t: "interaction_start", requestId, target, actionId });
  }

  cancelInteraction(requestId: string): void {
    this.send({ t: "interaction_cancel", requestId });
  }

  requestInventoryResync(): void {
    this.send({ t: "inventory_resync" });
  }

  physicalAction(message: ClientPhysicalAction): void {
    if (this.state === "online") this.send(message);
  }

  requestContainerResync(accessSessionId: string): void {
    this.send({ t: "container_resync", accessSessionId });
  }

  moveInventoryStack(requestId: string, expectedRevision: number, sourceStackId: string, destinationSlot: number): void {
    this.send({ t: "inventory_move", requestId, expectedRevision, sourceStackId, destinationSlot });
  }

  splitInventoryStack(
    requestId: string,
    expectedRevision: number,
    sourceStackId: string,
    quantity: number,
    destinationSlot: number,
  ): void {
    this.send({ t: "inventory_split", requestId, expectedRevision, sourceStackId, quantity, destinationSlot });
  }

  dropInventoryStack(requestId: string, expectedRevision: number, sourceStackId: string, quantity: number): void {
    this.send({ t: "inventory_drop", requestId, expectedRevision, sourceStackId, quantity });
  }

  transferContainerStack(
    requestId: string,
    accessSessionId: string,
    direction: ContainerTransferDirection,
    sourceStackId: string,
    quantity: number,
    expectedInventoryRevision: number,
    expectedContainerRevision: number,
    destinationPocket?: PocketRef,
  ): void {
    this.send({
      t: "container_transfer",
      requestId,
      accessSessionId,
      direction,
      sourceStackId,
      quantity,
      expectedInventoryRevision,
      expectedContainerRevision,
      ...(destinationPocket ? { destinationPocket } : {}),
    });
  }

  closeContainer(accessSessionId: string): void {
    this.send({ t: "container_close", accessSessionId });
  }

  developerGiveItem(definitionId: string, quantity: number): void {
    if (this.developer.authorized) this.send({
      t: "dev_cmd",
      command: "give_item",
      definitionId,
      quantity,
    });
  }

  developerClearInventory(): void {
    if (this.developer.authorized) this.send({ t: "dev_cmd", command: "clear_inventory" });
  }

  developerSpawnWorldItem(definitionId: string, quantity: number): void {
    if (this.developer.authorized) this.send({ t: "dev_cmd", command: "spawn_world_item", definitionId, quantity });
  }

  developerResetTestCache(): void {
    if (this.developer.authorized) this.send({ t: "dev_cmd", command: "reset_test_cache" });
  }

  developerGameplayCommand(command: ClientDeveloperCommand): void {
    if (this.developer.authorized) this.send(command);
  }

  estimatedServerTick(now = performance.now()): number {
    if (this.latest.tick < 0 || this.latest.receivedAt <= 0) return this.latest.tick;
    return this.latest.tick + Math.max(0, now - this.latest.receivedAt) * this.serverTickRate / 1_000;
  }

  get latestInputSequence(): number {
    return this.sequence;
  }

  get acknowledgedInputSequence(): number {
    return this.latest.self?.[5] ?? 0;
  }

  get pendingInputCount(): number {
    return Math.max(0, this.sequence - this.acknowledgedInputSequence);
  }

  get bufferedAmount(): number {
    return this.socket?.bufferedAmount ?? 0;
  }

  private send(message: ClientMessage): void {
    if (this.socket?.readyState !== WebSocket.OPEN) return;
    const serialized = JSON.stringify(message);
    this.bytesSent += serialized.length;
    this.socket.send(serialized);
  }

  private receive(serialized: string): void {
    this.bytesReceived += serialized.length;
    try {
      const message = JSON.parse(serialized) as ServerMessage;
      if (message.t === "welcome") {
        if (
          message.v !== PROTOCOL_VERSION ||
          message.itemCatalogVersion !== ITEM_CATALOG_VERSION ||
          message.itemCatalogHash !== ITEM_CATALOG_HASH ||
          message.seed !== WORLD_GENERATION_SEED ||
          message.generationVersion !== WORLD_GENERATION_VERSION
        ) {
          this.desired = false;
          this.onUpdateRequired?.();
          this.socket?.close(1002, "World generation protocol mismatch");
          return;
        }
        if (message.id !== this.sessionId) this.resetSession();
        this.handshakeFailures = 0;
        this.sessionId = message.id;
        this.serverTickRate = message.tickRate;
        this.updateDeveloperState(message.developer);
        this.setState("online");
        if (this.pingTimer !== undefined) window.clearInterval(this.pingTimer);
        this.pingTimer = window.setInterval(() => this.send({ t: "ping", at: performance.now() }), 4_000);
        this.send({ t: "ping", at: performance.now() });
      } else if (message.t === "combat") {
        if (isServerCombatSnapshot(message)) { this.combatBytesReceived += serialized.length; this.onCombat?.(message); }
      } else if (message.t === "combat_events") {
        if (isServerCombatEvents(message)) { this.combatBytesReceived += serialized.length; this.onCombatEvents?.(message); }
      } else if (message.t === "condition") {
        if (isServerPlayerCondition(message)) { this.conditionBytesReceived += serialized.length; this.onCondition?.(message); }
      } else if (message.t === "environment") {
        if (isServerEnvironment(message)) this.onEnvironment?.(message);
      } else if (message.t === "lightning") {
        if (isServerLightning(message)) this.onLightning?.(message.event);
      } else if (message.t === "guest_status") {
        if (!Number.isFinite(message.idleRemainingMs) || message.idleRemainingMs < 0) return;
        this.onGuestStatus?.(message);
        if (message.reason === "IDLE_TIMEOUT") this.disconnect();
      } else if (message.t === "npc_conversation" || message.t === "npc_reply_start"
        || message.t === "npc_reply_delta" || message.t === "npc_reply_final"
        || message.t === "npc_player_message" || message.t === "npc_error") {
        this.onNpcMessage?.(message);
      } else if (message.t === "pong") {
        this.ping = Math.max(0, performance.now() - message.at);
      } else if (message.t === "drone_telemetry") {
        if (isServerDroneTelemetry(message)) this.onDroneTelemetry?.(message);
      } else if (message.t === "drone_shout_result") {
        if (isServerDroneShoutResult(message)) this.onDroneShoutResult?.(message);
      } else if (message.t === "dev_state") {
        if (message.teleport && this.latest.self) {
          const self = this.latest.self;
          this.latest = {
            ...this.latest,
            self: [self[0], message.teleport.x, message.teleport.y, message.teleport.z, self[4], self[5],
              classifyWaterLocomotion(sampleWorldWaterAt(message.teleport.x, message.teleport.z).depth), self[7], 0, 0, 0, self[11], self[12], self[13], self[14], 0, self[16], self[17], 0, 0, self[20], self[21] + 1, self[22]],
          };
        }
        this.updateDeveloperState(message.state, message.teleport);
      } else if (message.t === "error") {
        this.onDeveloperError?.(message.code);
      } else if (message.t === "interaction_started") {
        this.onInteractionStarted?.(message);
      } else if (message.t === "interaction_completed") {
        this.onInteractionCompleted?.(message);
      } else if (message.t === "interaction_cancelled") {
        this.onInteractionCancelled?.(message);
      } else if (message.t === "interaction_rejected") {
        this.onInteractionRejected?.(message);
      } else if (message.t === "world_override_sync") {
        this.onWorldOverrideSync?.(message);
      } else if (message.t === "world_override_delta") {
        this.onWorldOverrideDelta?.(message);
      } else if (
        message.t === "inventory_sync"
        || message.t === "physical_sync"
        || message.t === "physical_result"
        || message.t === "inventory_delta"
        || message.t === "inventory_result"
        || message.t === "container_sync"
        || message.t === "container_delta"
        || message.t === "container_closed"
        || message.t === "world_drop_sync"
        || message.t === "world_drop_spawn"
        || message.t === "world_drop_remove"
      ) {
        this.onInventoryMessage?.(message);
      } else if (message.t === "snap") {
        if (!Number.isSafeInteger(message.tick) || message.tick < 0 || !Number.isFinite(message.at)
          || (this.latest.generation === this.connectionGeneration && message.tick <= this.latest.tick)) return;
        if (!isDroneSnapshotList(message.drones) || !Array.isArray(message.players) || !message.players.every(isPackedPlayer)) return;
        for (const [id, appearance] of message.appearances ?? []) this.appearances.set(id, appearance);
        const players: HydratedPlayer[] = message.players.map((player) => [
          ...player,
          this.appearances.get(player[0]) ?? DEFAULT_APPEARANCE,
        ]);
        const relevantIds = new Set(players.map((player) => player[0]));
        for (const id of this.appearances.keys()) {
          if (!relevantIds.has(id)) this.appearances.delete(id);
        }
        this.latest = {
          self: players.find((player) => player[0] === this.sessionId),
          others: players.filter((player) => player[0] !== this.sessionId),
          generation: this.connectionGeneration,
          tick: message.tick,
          serverAt: message.at,
          receivedAt: performance.now(),
        };
        this.onDroneSnapshot?.(message.drones, message.at);
        this.onEquipment?.(message.equipment ?? []);
        this.onNpcSnapshot?.(message.npcs ?? [], message.at, message.tick);
      }
    } catch {
      // A malformed snapshot is ignored; the next authoritative snapshot replaces it.
    }
  }

  private disconnected(): void {
    const failedHandshake = this.state !== "online";
    this.socket = undefined;
    if (this.pingTimer !== undefined) window.clearInterval(this.pingTimer);
    this.resetSession();
    if (!this.desired) return this.setState("offline");
    if (failedHandshake) this.handshakeFailures += 1;
    if (this.handshakeFailures >= 3 && this.onGuestEntryRequired) {
      this.desired = false;
      this.setState("offline");
      this.onGuestEntryRequired();
      return;
    }
    this.retry += 1;
    this.reconnectCount += 1;
    this.setState("reconnecting");
    const delay = Math.min(8_000, 500 * 2 ** Math.min(this.retry, 4)) + Math.random() * 300;
    this.reconnectTimer = window.setTimeout(() => this.connect(), delay);
  }

  private resetSession(): void {
    this.sessionId = "";
    this.latest = {
      self: undefined,
      others: [],
      generation: this.connectionGeneration,
      tick: -1,
      serverAt: 0,
      receivedAt: 0,
    };
    this.appearances.clear();
    this.onEquipment?.([]);
    this.serverTickRate = 20;
    this.updateDeveloperState(DEFAULT_DEVELOPER_STATE);
    this.onSessionReset?.();
    this.onDroneReset?.();
    this.onNpcReset?.();
  }

  private updateDeveloperState(state: DeveloperState, teleport?: DeveloperTeleportResult): void {
    this.developer = Object.freeze({ ...state });
    this.onDeveloperState?.(this.developer, teleport);
  }

  private setState(state: ConnectionState): void {
    if (this.state === state) return;
    this.state = state;
    this.onStateChange?.(state);
  }
}

function round(value: number): number {
  return Math.round(value * 1_000) / 1_000;
}
